import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Download } from 'lucide-react';
import { callsAPI, workspaceAPI } from '../../api/client';
import { apiError } from '../../lib/host';
import { formatDuration } from './calls/callModel';
import { ErrorBanner, PageHeading, saveDownload, StatCards, useBasic } from './ui';

const BAR_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#9333ea', '#f43f5e', '#0ea5e9', '#718096'];
const CONTACT_STATUS = [['new', 'New', '#10b981'], ['hot', 'Hot', '#f43f5e'], ['warm', 'Warm', '#f59e0b'], ['cold', 'Cold', '#2563eb']];
const APPT_STATUS = [['scheduled', 'Scheduled', '#f59e0b'], ['confirmed', 'Confirmed', '#10b981'], ['completed', 'Completed', '#2563eb'], ['cancelled', 'Cancelled', '#f43f5e']];

function Bars({ rows, empty }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (!rows.some((r) => r.count)) return <p className="panel-empty">{empty}</p>;
  return (
    <div className="bar-list">
      {rows.map((r, i) => (
        <div className="bar-row" key={r.label}>
          <span className="bar-label">{r.label}</span>
          <div className="bar-track"><i style={{ width: `${(r.count / max) * 100}%`, background: r.color || BAR_COLORS[i % BAR_COLORS.length] }} /></div>
          <b>{r.count}</b>{r.percent != null && <small>{r.percent}%</small>}
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { notify } = useBasic();
  const [summary, setSummary] = useState(null);
  const [sources, setSources] = useState(null);
  const [calls, setCalls] = useState(null);
  const [error, setError] = useState('');
  const sourcesRef = useRef(null);

  useEffect(() => {
    workspaceAPI.summary().then((res) => setSummary(res.data)).catch((err) => setError(apiError(err, 'Could not load analytics')));
    workspaceAPI.leadSources().then((res) => setSources(res.data)).catch(() => setSources([]));
    callsAPI.stats().then((res) => setCalls(res.data)).catch(() => setCalls({ answered_this_month: 0, missed_this_month: 0, inbound_this_month: 0, outbound_this_month: 0 }));
  }, []);

  const download = async () => {
    try {
      saveDownload(await workspaceAPI.report(), 'report');
      notify('Report downloaded.');
    } catch (err) {
      setError(apiError(err, 'Could not download the report'));
    }
  };

  const showSources = () => sourcesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const rate = summary?.completion_rate;

  return (
    <section className="page-view">
      <PageHeading title="Analytics" subtitle="Understand your leads, appointments, and team activity.">
        <button className="primary-action" onClick={download}><Download size={14} />Download report</button>
      </PageHeading>
      <ErrorBanner message={error} />

      <StatCards stats={[
        ['Total contacts', summary?.contacts_total, 'All time'],
        ['Contacts added', summary?.contacts_this_month, 'This month'],
        ['Appointments', summary?.appointments_this_month, 'This month'],
        ['Completion rate', summary ? (rate == null ? '—' : `${rate}%`) : null, 'Completed vs cancelled, this month'],
      ]} />

      <div className="analytics-grid">
        <section className="panel page-info-card" ref={sourcesRef}>
          <div className="panel-heading"><h2>Lead sources</h2><button className="text-button" onClick={() => navigate('/contacts')}>Contacts <ArrowRight size={13} /></button></div>
          {sources === null ? <p className="panel-empty">Loading…</p>
            : <Bars rows={sources.map((s) => ({ label: s.source, count: s.count, percent: s.percent }))} empty="No contacts yet. Lead sources appear once contacts are added." />}
        </section>
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Contacts by status</h2></div>
          {summary ? <Bars rows={CONTACT_STATUS.map(([k, label, color]) => ({ label, color, count: summary.contacts_by_status[k] }))} empty="No contacts yet." /> : <p className="panel-empty">Loading…</p>}
        </section>
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Appointments this month</h2><button className="text-button" onClick={() => navigate('/appointments')}>Appointments <ArrowRight size={13} /></button></div>
          {summary ? <Bars rows={APPT_STATUS.map(([k, label, color]) => ({ label, color, count: summary.appointments_by_status[k] }))} empty="No appointments this month." /> : <p className="panel-empty">Loading…</p>}
        </section>
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Calls this month</h2><button className="text-button" onClick={() => navigate('/calls')}>Calls <ArrowRight size={13} /></button></div>
          {calls ? (
            <>
              <Bars rows={[
                { label: 'Answered', color: '#10b981', count: calls.answered_this_month },
                { label: 'Missed', color: '#f43f5e', count: calls.missed_this_month },
                { label: 'Incoming', color: '#2563eb', count: calls.inbound_this_month },
                { label: 'Outgoing', color: '#9333ea', count: calls.outbound_this_month },
              ]} empty="No calls this month yet." />
              {calls.avg_duration_seconds != null && <p className="access-copy">Average answered call: {formatDuration(calls.avg_duration_seconds)}</p>}
            </>
          ) : <p className="panel-empty">Loading…</p>}
        </section>
      </div>

      <div className="page-bottom-grid">
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Quick actions</h2></div>
          <div className="page-action-list">
            <button onClick={download}><span className="action-dot dot-0">↓</span><span><b>Download report</b><small>CSV summary of contacts and appointments</small></span><ArrowRight size={14} /></button>
            <button onClick={() => navigate('/calls')}><span className="action-dot dot-1">↗</span><span><b>Call performance</b><small>Call history, outcomes and export</small></span><ArrowRight size={14} /></button>
            <button onClick={showSources}><span className="action-dot dot-2">◔</span><span><b>Lead sources</b><small>Where your contacts come from</small></span><ArrowRight size={14} /></button>
          </div>
        </section>
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>About these numbers</h2></div>
          <p className="access-copy">Contact and appointment figures are live from your company's data. "This month" follows UTC dates.</p>
        </section>
      </div>
    </section>
  );
}
